import React, { useState, useContext } from 'react';
import moment from 'moment';
import axios from "axios";
import { Popover, Button, OverlayTrigger, Modal, Form, Toast } from 'react-bootstrap';
import "./AuditoriaTable.css";
import { AuthContext } from '../../../../contexts/AuthContext';
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import * as links from "../../../../utils/links";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import jsPDF from "jspdf";

const AuditoriaTable = ({ pagamentos, updateTable }) => {
  // Estados para controle do modal e do popover
  const [showModal, setShowModal] = useState(false);
  const [selectedMensal, setSelectedMensal] = useState(null);
  const [selectedDelete, setSelectedDelete] = useState(null);
  const { setDataUser, authInfo } = useContext(AuthContext);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { dataUser } = authInfo;
  let navigate = useNavigate();
  const token = authInfo?.dataUser?.token;

  // Estado para controlar qual popover está visível (identificado pelo índice)
  const [activePopover, setActivePopover] = useState(null);

  // Função para abrir o modal com os dados da linha
  const handleEdit = (mensal) => {
    setSelectedMensal(mensal);
    setShowModal(true);
    setActivePopover(null); // Fecha o popover ao abrir o modal
  };

  // Função para fechar o modal
  const handleClose = () => {
    setShowModal(false);
    setSelectedMensal(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedMensal((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const updateParcela = (selectedMensal) => {
    let body = {
      dataDeVencimento: selectedMensal.dataDeVencimento,
      dataDeRenovacao: selectedMensal.dataDeRenovacao,
      valor: selectedMensal.valor,
      status: selectedMensal.status,
      dataDoPagamento: selectedMensal.dataDoPagamento,
      diaPagamento: selectedMensal.diaPagamento,
    };
    axios
      .put(`${process.env.REACT_APP_SERVIDOR}/parcela/${selectedMensal?.id}`, body, {
        headers: {
          "x-access-token": token,
          "content-type": "application/json",
        },
      })
      .then((res) => {
        toast.success("Alterações realizadas com sucesso!");
        handleClose();
        updateTable();
      })
      .catch((err) => {
        if ([401, 403].includes(err.response.status)) {
          toast.error("A sua sessão expirou, para continuar faça login novamente.");
          if (dataUser.key === "ADMIN") {
            navigate(links.ADMIN_SIGNIN);
            setDataUser(null);
          } else {
            navigate(links.SIGNIN);
            setDataUser(null);
          }
        }
        toast.error(`${err?.response?.data?.error}`);
      });
  };

  const deleteParcela = (selectedMensal) => {
    axios
      .delete(`${process.env.REACT_APP_SERVIDOR}/parcela/${selectedMensal?.id}`, {
        headers: {
          "x-access-token": token,
        },
      })
      .then((res) => {
        toast.success(`${res?.data?.message}`);
        setShowDeleteConfirm(false);
        setShowModal(false);
        updateTable();
      })
      .catch((err) => {
        toast.error(`${err?.response?.data?.error}`);
      });
  };

  const handleDelete = () => {
    deleteParcela(selectedDelete);
  };

  const handleExportPdf = async (pagamento) => {
    if (!pagamento?.id) return;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_SERVIDOR}/auditoria-pagamentos-detalhe/${pagamento.id}`,
        {
          headers: {
            "x-access-token": token,
            "content-type": "application/json",
          },
        }
      );

      const detalhe = res?.data || {};
      const p = detalhe?.pagamento || {};
      const mp = detalhe?.mercadopago || {};

      const doc = new jsPDF();
      let y = 14;

      const isObject = (v) => v !== null && typeof v === "object";

      const toPrintable = (v) => {
        if (v === undefined || v === null) return "-";
        if (typeof v === "string") return v.trim() === "" ? "-" : v;
        if (typeof v === "number") return Number.isFinite(v) ? String(v) : "-";
        if (typeof v === "boolean") return v ? "true" : "false";
        try {
          if (v instanceof Date) return moment(v).format("DD/MM/YYYY HH:mm:ss");
        } catch (_) {}
        if (isObject(v)) {
          try {
            return JSON.stringify(v);
          } catch (_) {
            return String(v);
          }
        }
        return String(v);
      };

      const addLine = (label, value) => {
        const safeValue = toPrintable(value);
        const text = `${label}: ${safeValue}`;
        const lines = doc.splitTextToSize(text, 190);
        for (const line of lines) {
          if (y > 285) {
            doc.addPage();
            y = 14;
          }
          doc.text(line, 10, y);
          y += 7;
        }
      };

      const addTitle = (title) => {
        if (y > 275) {
          doc.addPage();
          y = 14;
        }
        doc.setFontSize(12);
        doc.text(title, 10, y);
        y += 8;
        doc.setFontSize(10);
      };

      const flatten = (obj, prefix = "", out = []) => {
        if (!isObject(obj)) return out;
        if (Array.isArray(obj)) {
          obj.forEach((item, idx) => {
            const nextPrefix = prefix ? `${prefix}[${idx}]` : `[${idx}]`;
            if (isObject(item)) flatten(item, nextPrefix, out);
            else out.push({ key: nextPrefix, value: item });
          });
          return out;
        }

        Object.keys(obj).sort().forEach((k) => {
          const v = obj[k];
          const nextPrefix = prefix ? `${prefix}.${k}` : k;
          if (isObject(v)) flatten(v, nextPrefix, out);
          else out.push({ key: nextPrefix, value: v });
        });
        return out;
      };

      doc.setFontSize(14);
      doc.text("Auditoria de Pagamento", 10, y);
      y += 10;
      doc.setFontSize(10);

      addTitle("Resumo");
      addLine("ID Interno", p?.id);
      addLine("ID Mercado Pago", p?.mercadoPagoId ?? mp?.id);
      addLine("Data", p?.data ? moment(p?.data).format("DD/MM/YYYY HH:mm:ss") : "-");
      addLine("Tipo", p?.tipo);
      addLine("Valor", new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(p?.valor || 0)));
      addLine("Taxas", new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(p?.taxas || 0)));
      addLine("Estornado", p?.estornado ? "Sim" : "Não");
      addLine("Status (Sistema)", p?.status);
      const statusSistema = String(p?.status || "").toUpperCase();
      const statusMp = String(mp?.status || "").toLowerCase();
      const recebidoPeloSistema = !p?.estornado && !!(p?.mercadoPagoId ?? mp?.id);
      const liberacaoSistema =
        recebidoPeloSistema ||
        (statusMp === "approved" ||
          ["CONFIRMADO", "PENDENTE", "RECEBIDO", "APROVADO"].includes(statusSistema));
      addLine(
        "Liberação do Produto (Sistema)",
        liberacaoSistema ? "Sim" : "Não"
      );
      addLine("Bônus (Sistema)", p?.valorBonus);
      addLine("Status", mp?.status);
      addLine("Status Detail", mp?.status_detail);

      addTitle("Detalhes (Pagamento)");
      flatten(p)
        .filter(({ key }) => {
          const rootKey = String(key).split(".")[0];
          return rootKey !== "maquina" && rootKey !== "cliente";
        })
        .forEach(({ key, value }) => addLine(key, value));

      addTitle("Detalhes (Mercado Pago)");
      flatten(mp).forEach(({ key, value }) => addLine(key, value));

      const fileId = p?.mercadoPagoId || mp?.id || pagamento?.id;
      doc.save(`auditoria-${fileId}.pdf`);
    } catch (e) {
      toast.error("Erro ao gerar PDF");
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
      <table className="table table-bordered">
        <thead>
          <tr>
            <th className="text-muted">ID do Pagamento</th>
            <th className="text-muted">Data de Pagamento</th>
            <th className="text-muted">Tipo Pagamento</th>
            <th className="text-muted">Valor</th>
            <th className="text-muted">Detalhe</th>
            <th className="text-muted">PDF</th>
          </tr>
        </thead>
        <tbody>
          {pagamentos?.map((canal, index) => (
            <tr key={index}>
              <td>
                {canal.mercadoPagoId ?? '-'}
              </td>
              <td>
                {canal?.data
                  ? moment(canal?.data)?.format("DD/MM/YYYY HH:mm:ss")
                  : "-"}
              </td>
              <td>
                {canal?.tipo && canal?.tipo === 'debit_card' ? 'Débito' : canal?.tipo === 'credit_card' ? 'Crédito' : 'Pix'}
              </td>
              <td>
                {new Intl.NumberFormat("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                }).format(canal?.valor)}
              </td>
              <td>
                <Button
                  className="EditPagamentos_header_HelpPage"
                  onClick={() => {
                    navigate(links.DETALHE_AUDITORIA+'/'+canal.id, {
                      state: { redirect_url: `${links.ADD_MACHINE}` },
                    });
                  }}
                  // disabled={isLoading}
                >
                  Detalhe
                </Button>
              </td>
              <td>
                <Button
                  variant="secondary"
                  onClick={() => handleExportPdf(canal)}
                >
                  Exportar
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Modal de edição */}
      <Modal show={showModal} onHide={handleClose} centered style={{ textAlign: "center" }}>
        <Modal.Header closeButton className="modal-footer">
          <Modal.Title>{showDeleteConfirm ? "Excluir Mensalidade" : "Editar Mensalidade"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMensal ? (
            <Form>
              <Form.Group controlId="formDataDeVencimento">
                <Form.Label>Data de Vencimento</Form.Label>
                <Form.Control
                  type="date"
                  name="dataDeVencimento"
                  value={moment(selectedMensal.dataDeVencimento).format("YYYY-MM-DD")}
                  onChange={handleInputChange}
                />
              </Form.Group>

              <Form.Group controlId="formDataDeRenovacao" className="mt-3">
                <Form.Label>Data de Renovação</Form.Label>
                <Form.Control
                  type="date"
                  name="dataDeRenovacao"
                  value={moment(selectedMensal.dataDeRenovacao).format("YYYY-MM-DD")}
                  onChange={handleInputChange}
                />
              </Form.Group>

              <Form.Group controlId="formValor" className="mt-3">
                <Form.Label>Valor</Form.Label>
                <Form.Control
                  type="number"
                  name="valor"
                  value={selectedMensal.valor}
                  onChange={handleInputChange}
                />
              </Form.Group>

              <Form.Group controlId="formDataDePagamento" className="mt-3">
                <Form.Label>Data de Pagamento</Form.Label>
                <Form.Control
                  type="date"
                  name="dataDoPagamento"
                  value={moment(selectedMensal.dataDoPagamento).format("YYYY-MM-DD")}
                  onChange={handleInputChange}
                />
              </Form.Group>

              <Form.Group controlId="formDiaPagamento" className="mt-3">
                <Form.Label>Dia de Pagamento</Form.Label>
                <Form.Control
                  type="text"
                  name="diaPagamento"
                  value={selectedMensal.diaPagamento}
                  onChange={handleInputChange}
                />
              </Form.Group>

              <Form.Group controlId="formStatus" className="mt-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={selectedMensal.status}
                  onChange={handleInputChange}
                  className="form-control"
                  style={{ width: "100%", borderRadius: "8px" }}
                >
                  <option value="ABERTO">ABERTO</option>
                  <option value="PAGO">PAGO</option>
                  <option value="VENCIDO">VENCIDO</option>
                </Form.Select>
              </Form.Group>
            </Form>
          ) : showDeleteConfirm ? (
            <p style={{ fontWeight: "bold" }}>
              Tem certeza disso? Ao confirmar, esta mensalidade será excluída permanentemente.
            </p>
          ) : (
            ""
          )}
        </Modal.Body>
        <Modal.Footer className="modal-footer">
          {/* Botões de Confirmação de Exclusão ou Cancelamento */}
          {showDeleteConfirm ? (
            <>
              <Button variant="secondary" onClick={handleClose}>
                Cancelar
              </Button>
              <Button variant="danger" onClick={handleDelete}>
                Confirmar Exclusão
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={handleClose}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={() => updateParcela(selectedMensal)}>
                Salvar
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AuditoriaTable;
